import { ReactElement } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Alert, Heading, Link, Paragraph, Skeleton, Tag } from '@digdir/designsystemet-react';
import type { DeviationSummary } from '../../../api/safetyRounds';
import { useGetUserinfo } from '../../../hooks/queries/useGetUserinfo';
import { useMyDeviationsQuery } from '../../../hooks/queries/safetyRounds/useDeviationQueries';
import { longDate } from '../Plans/plansView';
import { notificationsPathFor } from '../Protocol/protocolView';
import { deviationPath, DUE_COLORS, STATUS_COLORS } from './deviationView';
import layout from '../../HRDashboard/hmsPage.module.css';
import classes from './Deviations.module.css';

/** «Mine avvik»: the deviations from safety rounds the user is responsible for */
export const MyDeviationsPage = (): ReactElement => {
  const { t } = useTranslation();
  const K = 'safetyRounds.myDeviations';
  const { data, isLoading, error } = useMyDeviationsQuery();
  const notificationsPath = notificationsPathFor(useGetUserinfo().data?.userRoles ?? []);

  const content = () => {
    if (isLoading)
      return (
        <div aria-busy="true">
          <span className="ds-sr-only">{t(`${K}.loading`)}</span>
          <Skeleton variant="rectangle" width="100%" height={240} />
        </div>
      );
    if (error || !data)
      return (
        <Alert data-color="danger">
          <Heading level={2} data-size="2xs">
            {t('safetyRounds.errors.loadTitle')}
          </Heading>
          <Paragraph>{t('safetyRounds.errors.loadMessage')}</Paragraph>
        </Alert>
      );
    return (
      <>
        <Group id="mine-todo" title={t(`${K}.toDo`)} empty={t(`${K}.toDoEmpty`)} items={data.toDo} />
        {data.waiting.length > 0 && (
          <Group id="mine-waiting" title={t(`${K}.waiting`)} empty="" items={data.waiting} />
        )}
        <Group id="mine-done" title={t(`${K}.done`)} empty={t(`${K}.doneEmpty`)} items={data.done} />
      </>
    );
  };

  return (
    <div className={layout.page} data-size="md">
      <section className={layout.hero}>
        <div aria-hidden="true" className={layout.backdrop} />
        <div className={layout.heroContent}>
          <span className={layout.eyebrow}>{t(`${K}.eyebrow`)}</span>
          <Heading level={1} data-size="xl">
            {t(`${K}.title`)}
          </Heading>
          <Paragraph className={layout.intro}>{t(`${K}.intro`)}</Paragraph>
          <Link asChild data-size="sm">
            <RouterLink to={notificationsPath}>{t('safetyRounds.notifications.choose')}</RouterLink>
          </Link>
        </div>
      </section>
      <div className={classes.content}>{content()}</div>
    </div>
  );
};

const Group = ({ id, title, empty, items }: { id: string; title: string; empty: string; items: DeviationSummary[] }) => {
  const { t, i18n } = useTranslation();
  return (
    <section aria-labelledby={id} className={classes.section}>
      <Heading level={2} data-size="sm" id={id}>
        {title} <span className={layout.subtle}>({items.length})</span>
      </Heading>
      {items.length === 0 ? (
        <Paragraph data-size="sm" className={layout.subtle}>
          {empty}
        </Paragraph>
      ) : (
        <ul className={classes.list}>
          {items.map((item) => (
            <li key={item.id} className={classes.item}>
              <Link asChild>
                <RouterLink to={deviationPath(item.id)}>
                  {item.number} {item.title}
                </RouterLink>
              </Link>
              <Paragraph data-size="sm" className={layout.subtle}>
                {item.planName}
              </Paragraph>
              <div className={classes.tags}>
                <Tag data-color={STATUS_COLORS[item.status]} data-size="sm">
                  {t(`safetyRounds.protocol.deviationStatus.${item.status}`)}
                </Tag>
                <Tag data-color={DUE_COLORS[item.dueState]} data-size="sm">
                  {t('safetyRounds.myDeviations.due', { date: longDate(item.dueDate, i18n.language) })}
                </Tag>
              </div>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
};
